import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useMemo } from 'react';
import './MapGrid.scss';
import { getCoordinateString, } from '../../utils/coordinates';
import { backendUrl, LockStatus, RetryStatus } from '@common/contracts';
import ScenarioOverlay from './ScenarioOverlay';
import { getCanonicalNodeTypeIconUrl } from '../NodePropertiesForm/nodeTypeIcons';
import { assetUrl } from '../../utils/img';
// Convert a #RRGGBB colour + 0-1 alpha into an rgba() string (falls back to the
// input for non-hex values). Used to bake the overlay opacity into the grid frame.
const hexToRgba = (hex, alpha) => {
    const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    if (!m)
        return hex;
    return `rgba(${parseInt(m[1], 16)}, ${parseInt(m[2], 16)}, ${parseInt(m[3], 16)}, ${alpha})`;
};
// Helper function to get node type image URL
const getNodeTypeImageUrl = (nodeType) => {
    if (!(nodeType === null || nodeType === void 0 ? void 0 : nodeType.image))
        return null;
    return nodeType.image.startsWith('http') ? nodeType.image : `${backendUrl}${nodeType.image}`;
};
// Helper function to get node status image URL
const getNodeStatusImageUrl = (nodeStatus) => {
    if (!(nodeStatus === null || nodeStatus === void 0 ? void 0 : nodeStatus.image))
        return null;
    return nodeStatus.image.startsWith('http') ? nodeStatus.image : `${backendUrl}${nodeStatus.image}`;
};
export function MapGrid({ width, height, cellSize, // Add to destructuring
selectedCoords, onNodeSelect, overlayOpacity, overlayColor, lineWidth, activeOverlays, playMode, nodes, // Add nodes to destructuring
nodeTypes, nodeStatuses, playSubMode, locksOutcomeType, levelId, events, scenarioDisplayOptions, highlightedCells, playerPosition, visitedCells, gameLockedCells, movableCells, onPlayerMove, }) {
    var _a, _b;
    // Removed internal selectedCell state, selection is now controlled by parent
    const [hoveredCell, setHoveredCell] = useState(null);
    // Play-preview lookups (empty/no-op when not playing).
    const movableSet = useMemo(() => new Set(movableCells !== null && movableCells !== void 0 ? movableCells : []), [movableCells]);
    const visitedSet = useMemo(() => new Set(visitedCells !== null && visitedCells !== void 0 ? visitedCells : []), [visitedCells]);
    const gameLockedSet = useMemo(() => new Set(gameLockedCells !== null && gameLockedCells !== void 0 ? gameLockedCells : []), [gameLockedCells]);
    // Function to check if overlay should be shown
    const shouldShowOverlay = (overlayType) => {
        return activeOverlays.has(overlayType);
    };
    const handleCellClick = (x, y) => {
        // In play preview, clicking a reachable neighbour moves the avatar there.
        if (onPlayerMove && movableSet.has(getCoordinateString(x, y))) {
            onPlayerMove(x, y);
            return;
        }
        onNodeSelect(x, y); // Call the callback passed from parent
    };
    const handleCellMouseEnter = (x, y) => {
        // Enable hover always
        setHoveredCell({ x, y });
    };
    const handleCellMouseLeave = () => {
        setHoveredCell(null);
    };
    // Function to get affected cells with action types for different Play modes
    const getAffectedCellsWithTypes = (x, y) => {
        const coordinate = getCoordinateString(x, y);
        const node = nodes[coordinate];
        if (!(node === null || node === void 0 ? void 0 : node.simpleEvents))
            return new Map();
        const affectedCells = new Map();
        const addCellAction = (cellCode, actionType) => {
            if (!affectedCells.has(cellCode)) {
                affectedCells.set(cellCode, new Set());
            }
            affectedCells.get(cellCode).add(actionType);
        };
        if (playSubMode === 'Locks') {
            node.simpleEvents.forEach(event => {
                // Check actions for the selected outcome (success or failure)
                const outcomeActions = locksOutcomeType === 'success' ? event.success.actions : event.failure.actions;
                outcomeActions.forEach(action => {
                    if (action.nodeCode) {
                        if (action.action === 'lock') {
                            addCellAction(action.nodeCode, 'lock');
                        }
                        else if (action.action === 'unlock') {
                            addCellAction(action.nodeCode, 'unlock');
                        }
                    }
                });
                // Also check actions for the opposite outcome to show mixed states
                const oppositeActions = locksOutcomeType === 'success' ? event.failure.actions : event.success.actions;
                oppositeActions.forEach(action => {
                    if (action.nodeCode) {
                        if (action.action === 'lock') {
                            addCellAction(action.nodeCode, 'lock');
                        }
                        else if (action.action === 'unlock') {
                            addCellAction(action.nodeCode, 'unlock');
                        }
                    }
                });
            });
        }
        else if (playSubMode === 'UX') {
            // Show cells that have UX panel actions
            node.simpleEvents.forEach(event => {
                const outcomeActions = locksOutcomeType === 'success' ? event.success.actions : event.failure.actions;
                outcomeActions.forEach(action => {
                    if (action.action === 'show_ux_panel') {
                        // For UX actions, highlight the current cell since the action originates from it
                        addCellAction(coordinate, 'ux');
                    }
                });
            });
        }
        else if (playSubMode === 'Missions') {
            // TODO: Implement Missions mode logic
            // This would show which cells affect mission completion
            // For now, return empty array
        }
        else if (playSubMode === 'All') {
            // Show all affected cells from all modes
            node.simpleEvents.forEach(event => {
                const successActions = event.success.actions;
                const failureActions = event.failure.actions;
                [...successActions, ...failureActions].forEach(action => {
                    if (action.action === 'show_ux_panel') {
                        // For UX actions, highlight the current cell since the action originates from it
                        addCellAction(coordinate, 'ux');
                    }
                    else if (action.nodeCode) {
                        // For lock/unlock actions, highlight the target cell
                        if (action.action === 'lock') {
                            addCellAction(action.nodeCode, 'lock');
                        }
                        else if (action.action === 'unlock') {
                            addCellAction(action.nodeCode, 'unlock');
                        }
                        else {
                            addCellAction(action.nodeCode, 'other');
                        }
                    }
                });
            });
        }
        return affectedCells;
    };
    // Create grid cells
    const cells = [];
    for (let y = 0; y < height; y++) {
        for (let x = 0; x < width; x++) {
            cells.push({ x, y });
        }
    }
    // Cells only draw border-bottom + border-right, so the grid's TOP and LEFT outer
    // edges are missing. Draw them as an inset box-shadow on the grid itself (no layout
    // shift, same width/colour). Opacity is baked into rgba to match the cell borders,
    // whose colour shows through the cell element's `opacity`.
    const lineColor = hexToRgba(overlayColor, overlayOpacity);
    const gridStyle = {
        gridTemplateColumns: `repeat(${width}, ${cellSize}px)`,
        gridTemplateRows: `repeat(${height}, ${cellSize}px)`,
        width: `${width * cellSize}px`, // Set explicit width
        height: `${height * cellSize}px`, // Set explicit height
        boxShadow: `inset ${lineWidth}px ${lineWidth}px 0 0 ${lineColor}`,
    };
    // Base SVG font size - make it relative to cell size?
    const svgFontSize = Math.max(8, Math.min(18, Math.round(cellSize * 0.3))); // Adjust multiplier as needed
    // Calculate border color using overlayColor and overlayOpacity
    const cellBorderStyle = {
        borderColor: overlayColor,
        borderWidth: `${lineWidth}px`,
        opacity: overlayOpacity
    };
    // Start/goal are the two structural anchors of a level; badge them on the map so
    // the author always sees where the run begins and where it culminates.
    const startTypeId = (_b = (_a = nodeTypes === null || nodeTypes === void 0 ? void 0 : nodeTypes.find(nt => nt.name === 'Start')) === null || _a === void 0 ? void 0 : _a.id) !== null && _b !== void 0 ? _b : null;
    return (_jsx("div", { className: "map-grid-container", children: _jsx("div", { className: "map-grid", style: gridStyle, children: cells.map(cell => {
                var _a, _b, _c, _d;
                const isSelected = (selectedCoords === null || selectedCoords === void 0 ? void 0 : selectedCoords.x) === cell.x && (selectedCoords === null || selectedCoords === void 0 ? void 0 : selectedCoords.y) === cell.y;
                const coordinate = getCoordinateString(cell.x, cell.y);
                // Get node data for this cell
                const node = nodes[coordinate];
                let nodeTypeName;
                if ((node === null || node === void 0 ? void 0 : node.typeId) != null && nodeTypes) {
                    const found = nodeTypes.find(nt => nt.id === node.typeId);
                    nodeTypeName = (_a = found === null || found === void 0 ? void 0 : found.name) !== null && _a !== void 0 ? _a : 'Empty';
                }
                else {
                    nodeTypeName = 'Empty';
                }
                // Find matching NodeStatus based on rules:
                // 1. If initial lock = Locked, show NodeStatus with Initial Lock Locked binding
                // 2. If initial lock = Unlocked and has Simple Events (Quick Scenarios), show NodeStatus with Allow Retry binding
                let matchingNodeStatus = null;
                let nodeStatusImageUrl = null;
                if (nodeTypeName !== 'Empty' && nodeStatuses && node) {
                    // During a live run the lock is DYNAMIC: follow the run's locked set
                    // (playLive.lockedCells → gameLockedSet) so the icon flips to unlocked
                    // the moment a cell opens. Off-run it's the static design lock.
                    const isLivePlay = playerPosition != null;
                    const lockedNow = isLivePlay ? gameLockedSet.has(coordinate) : (node.initialLock === LockStatus.Locked);
                    // Rule 1: locked (now) → Initial Lock binding
                    if (lockedNow) {
                        matchingNodeStatus = nodeStatuses.find(ns => ns.initialLockBinding === LockStatus.Locked) || null;
                    }
                    // Rule 2: NOT locked (now) and has Simple Events (Quick Scenarios)
                    else if (node.simpleEvents && node.simpleEvents.length > 0) {
                        // Determine the Allow Retry state from Simple Events
                        // If any Simple Event has allowRetry = false, show "Allow Retry - No"
                        // Otherwise, show "Allow Retry - Yes" (default behavior)
                        const hasDisallowedRetry = node.simpleEvents.some(event => event.allowRetry === false);
                        const retryBinding = hasDisallowedRetry ? RetryStatus.No : RetryStatus.Yes;
                        matchingNodeStatus = nodeStatuses.find(ns => ns.allowRetryBinding === retryBinding) || null;
                    }
                    if (matchingNodeStatus) {
                        nodeStatusImageUrl = getNodeStatusImageUrl(matchingNodeStatus);
                    }
                }
                // Determine cell background based on viewMode
                let cellBackgroundStyle = {};
                let nodeTypeImageUrl = null;
                if (nodeTypeName !== 'Empty' && nodeTypes) {
                    const nodeType = nodeTypes.find(nt => nt.id === (node === null || node === void 0 ? void 0 : node.typeId));
                    // The DB image is the single source of truth (seeded from the game
                    // art, admin-editable); fall back to the bundled canonical glyph only
                    // when a type has no stored image.
                    nodeTypeImageUrl = (_c = (_b = getNodeTypeImageUrl(nodeType)) !== null && _b !== void 0 ? _b : getCanonicalNodeTypeIconUrl(nodeType === null || nodeType === void 0 ? void 0 : nodeType.name)) !== null && _c !== void 0 ? _c : null;
                }
                // Removed old Types overlay background logic - now using new approach with img elements
                // Set transparent background for Types mode since we now use img elements
                if (shouldShowOverlay('Types')) {
                    cellBackgroundStyle = { backgroundColor: 'transparent' };
                }
                else {
                    // For other modes or if no image, use transparent background
                    cellBackgroundStyle = { backgroundColor: 'transparent' };
                }
                // Art overlay – show node type image if present
                if (shouldShowOverlay('Art') && nodeTypeImageUrl) {
                    cellBackgroundStyle = Object.assign(Object.assign({}, cellBackgroundStyle), { backgroundImage: `url(${nodeTypeImageUrl})`, backgroundSize: 'cover', backgroundPosition: 'center', backgroundRepeat: 'no-repeat', opacity: overlayOpacity });
                }
                // Check if this cell is affected by locks mode
                const affectedCellsMap = hoveredCell ? getAffectedCellsWithTypes(hoveredCell.x, hoveredCell.y) : new Map();
                const cellActionTypes = affectedCellsMap.get(coordinate);
                const isAffected = cellActionTypes !== undefined;
                const isHovered = (hoveredCell === null || hoveredCell === void 0 ? void 0 : hoveredCell.x) === cell.x && (hoveredCell === null || hoveredCell === void 0 ? void 0 : hoveredCell.y) === cell.y;
                // Check if this cell is in highlighted cells (for static highlighting)
                const isHighlighted = (_d = highlightedCells === null || highlightedCells === void 0 ? void 0 : highlightedCells.cells.includes(coordinate)) !== null && _d !== void 0 ? _d : false;
                // Additional styling for special modes and highlighting
                let additionalStyle = {};
                // Highlight cells logic (works independently of play mode)
                if (isHighlighted) {
                    // Static highlight effect for highlighted cells (no animation)
                    let highlightColor;
                    switch (highlightedCells.type) {
                        case 'success':
                        case 'unlock':
                            highlightColor = '#34d399'; // Green for success and unlock
                            break;
                        case 'failure':
                        case 'lock':
                            highlightColor = '#ef4444'; // Red for failure and lock
                            break;
                        default:
                            highlightColor = '#34d399'; // Fallback green
                    }
                    additionalStyle = {
                        backgroundColor: `${highlightColor}99`, // 60% opacity for good visibility
                        boxShadow: `inset 0 0 0 2px ${highlightColor}DD` // More pronounced inner border
                    };
                }
                // Play mode specific styling (only when play mode is active)
                else if (playMode) {
                    if (isHovered) {
                        additionalStyle = {
                            backgroundColor: 'rgba(0, 191, 255, 0.3)', // Orange for hovered cell
                            outline: '2px solid var(--vm-cyan)',
                            outlineOffset: '-2px'
                        };
                    }
                    else if (isAffected && cellActionTypes) {
                        const hasLock = cellActionTypes.has('lock');
                        const hasUnlock = cellActionTypes.has('unlock');
                        const hasUx = cellActionTypes.has('ux');
                        if (playSubMode === 'Locks') {
                            if (hasLock && hasUnlock) {
                                // Diagonal red-green highlight for mixed states
                                additionalStyle = {
                                    background: 'linear-gradient(45deg, #ef444455 0%, #ef444455 50%, #34d39955 50%, #34d39955 100%)',
                                    boxShadow: 'inset 2px 2px 0 #ef4444, inset -2px -2px 0 #34d399'
                                };
                            }
                            else if (hasLock) {
                                // Red color for lock
                                additionalStyle = {
                                    backgroundColor: '#ef444433', // 20% opacity red
                                    outline: '2px solid #ef4444',
                                    outlineOffset: '-2px'
                                };
                            }
                            else if (hasUnlock) {
                                // Green color for unlock
                                additionalStyle = {
                                    backgroundColor: '#34d39933', // 20% opacity green
                                    outline: '2px solid #34d399',
                                    outlineOffset: '-2px'
                                };
                            }
                        }
                        else if (playSubMode === 'UX') {
                            additionalStyle = {
                                backgroundColor: '#2196F333', // Blue for UX
                                outline: '2px solid #2196F3',
                                outlineOffset: '-2px'
                            };
                        }
                        else if (playSubMode === 'Missions') {
                            additionalStyle = {
                                backgroundColor: '#34d39933', // Green for missions
                                outline: '2px solid #34d399',
                                outlineOffset: '-2px'
                            };
                        }
                        else if (playSubMode === 'All') {
                            // For the All mode, show the priority color
                            if (hasLock && hasUnlock) {
                                // Diagonal highlight
                                additionalStyle = {
                                    background: 'linear-gradient(45deg, #ef444455 0%, #ef444455 50%, #34d39955 50%, #34d39955 100%)',
                                    boxShadow: 'inset 2px 2px 0 #ef4444, inset -2px -2px 0 #34d399'
                                };
                            }
                            else if (hasLock) {
                                additionalStyle = {
                                    backgroundColor: '#ef444433',
                                    outline: '2px solid #ef4444',
                                    outlineOffset: '-2px'
                                };
                            }
                            else if (hasUnlock) {
                                additionalStyle = {
                                    backgroundColor: '#34d39933',
                                    outline: '2px solid #34d399',
                                    outlineOffset: '-2px'
                                };
                            }
                            else if (hasUx) {
                                additionalStyle = {
                                    backgroundColor: '#2196F333',
                                    outline: '2px solid #2196F3',
                                    outlineOffset: '-2px'
                                };
                            }
                            else {
                                additionalStyle = {
                                    backgroundColor: '#9C27B033', // Purple for other
                                    outline: '2px solid #9C27B0',
                                    outlineOffset: '-2px'
                                };
                            }
                        }
                    }
                }
                // One rule, no history: a cell shows a MARKER (an obstacle token, the
                // start, the goal) or it shows the hatch. So deleting an obstacle
                // hatches its cell on the spot — the author never has to tidy up a
                // cell that looks occupied but is not, and an emptied cell is
                // indistinguishable from one that was always empty.
                const hasObstacle = Array.isArray(node === null || node === void 0 ? void 0 : node.simpleEvents) && node.simpleEvents.length > 0;
                const isStartCell = startTypeId != null && (node === null || node === void 0 ? void 0 : node.typeId) === startTypeId;
                const isEmptyCell = !hasObstacle && !isStartCell && !(node === null || node === void 0 ? void 0 : node.isGoal);
                const isPlayer = !!playerPosition && coordinate === playerPosition;
                const isMovableTo = movableSet.has(coordinate);
                const isPlayVisited = visitedSet.has(coordinate) && !isPlayer;
                const isGameLocked = gameLockedSet.has(coordinate);
                return (_jsxs("div", { className: `map-cell ${isSelected ? 'selected' : ''} ${(node === null || node === void 0 ? void 0 : node.imageUrl) ? 'has-image' : ''} ${isAffected ? 'affected' : ''} ${isHovered ? 'hovered' : ''} ${isEmptyCell ? 'is-wall' : ''} ${isPlayer ? 'player-current' : ''} ${isPlayVisited ? 'player-visited' : ''} ${isGameLocked ? 'game-locked' : ''} ${isMovableTo ? 'movable-to' : ''}`, onClick: () => handleCellClick(cell.x, cell.y), onMouseEnter: () => handleCellMouseEnter(cell.x, cell.y), onMouseLeave: handleCellMouseLeave, title: `Cell ${coordinate} (${cell.x}, ${cell.y})`, style: Object.assign(Object.assign(Object.assign({}, cellBorderStyle), cellBackgroundStyle), additionalStyle), children: [isPlayer && _jsx("div", { className: "play-player-token", title: "Agent" }), shouldShowOverlay('Art') && (node === null || node === void 0 ? void 0 : node.imageUrl) && (_jsx("img", { src: assetUrl(node.imageUrl), alt: `Node ${coordinate}`, className: "map-cell-image", style: { opacity: overlayOpacity }, loading: "lazy", decoding: "async" })), shouldShowOverlay('Types') && nodeTypeImageUrl && (_jsx("div", { className: "node-type-with-status-container", children: _jsxs("div", { className: "node-type-black-circle", style: { opacity: overlayOpacity }, children: [_jsx("img", { src: nodeTypeImageUrl, alt: `Node Type ${nodeTypeName}`, className: "node-type-image-reduced" }), nodeStatusImageUrl && matchingNodeStatus && (_jsx("img", { src: nodeStatusImageUrl, alt: `Node Status ${matchingNodeStatus.name}`, className: "node-status-overlay" }))] }) })), shouldShowOverlay('Codes') && (_jsx("svg", { className: "map-cell-coordinate-svg", viewBox: "0 0 100 100", preserveAspectRatio: "xMidYMid meet", 
                            // Apply overlayOpacity to the SVG element
                            style: { opacity: overlayOpacity }, children: _jsx("text", { x: "50%", y: "50%", dy: "0.35em", textAnchor: "middle", lengthAdjust: "spacingAndGlyphs", textLength: "80", fontSize: svgFontSize, 
                                // Use overlayColor for text, but keep its opacity controlled by the SVG opacity
                                fill: overlayColor, children: coordinate }) })), shouldShowOverlay('Scenarios') && node && (_jsx(ScenarioOverlay, { coordinateString: coordinate, nodeData: node, cellSize: cellSize, overlayOpacity: overlayOpacity, levelId: levelId, events: events, displayOptions: scenarioDisplayOptions || {
                                showAbility: true,
                                showCost: true,
                                showDifficulty: true,
                                showReward: true,
                                showPenalty: true
                            } })), (node === null || node === void 0 ? void 0 : node.isGoal) && (_jsx("div", { className: "map-cell-anchor is-goal", title: "Story goal", children: "\u2691" })), !(node === null || node === void 0 ? void 0 : node.isGoal) && startTypeId != null && (node === null || node === void 0 ? void 0 : node.typeId) === startTypeId && (_jsx("div", { className: "map-cell-anchor is-start", title: "Start point", children: "\u2605" }))] }, `${cell.x}-${cell.y}`));
            }) }) }));
}
